import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getPublicInfo,
  listFanCharacters,
  listMyOwnedCharacters,
  type FanCharacterCard,
  type PublicCharacterInfo,
} from '../api/fan-api';
import { fanCognitoConfigured, getStoredFanClaims } from '../auth/fan-auth';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useInViewport } from '../lib/useInViewport';
import { SimpleGlbViewer } from '../components/SimpleGlbViewer';
import { Character3DStage } from '../components/Character3DStage';

type Tab = 'mine' | 'all';

function SkeletonCard() {
  return (
    <div
      aria-hidden="true"
      className="rounded-xl border border-slate-800 bg-slate-900 overflow-hidden"
    >
      <div className="aspect-[3/4] sm:aspect-[4/3] nx-skeleton-shimmer" />
      <div className="p-2 sm:p-3 space-y-2">
        <div className="h-4 w-3/4 rounded nx-skeleton-shimmer" />
        <div className="h-3 w-1/2 rounded nx-skeleton-shimmer" />
      </div>
    </div>
  );
}

function CharacterTile({ c, owned, signedIn }: { c: FanCharacterCard; owned: boolean; signedIn: boolean }) {
  const { ref, inView } = useInViewport();
  const [publicInfo, setPublicInfo] = useState<PublicCharacterInfo | null>(null);
  const [publicInfoFailed, setPublicInfoFailed] = useState(false);
  const is3D = c.type === 'model3d' && !!c.modelPath;
  const comingSoon = c.modelDistribution?.mode === 'coming-soon';
  const lockedForRelease = comingSoon && !owned;
  useEffect(() => {
    if (!is3D || !inView || publicInfo || publicInfoFailed) return;
    let alive = true;
    void getPublicInfo(c.id)
      .then((info) => { if (alive) setPublicInfo(info); })
      .catch(() => { if (alive) setPublicInfoFailed(true); });
    return () => { alive = false; };
  }, [c.id, inView, is3D, publicInfo, publicInfoFailed]);
  // Anonymous visitors get redirected to login when they tap a tile,
  // with `next` preserving the intended character so they land back
  // on the right page after auth.
  const target = signedIn ? `/c/${c.id}` : `/login?next=${encodeURIComponent(`/c/${c.id}`)}`;
  return (
    <Link
      ref={ref as unknown as React.Ref<HTMLAnchorElement>}
      to={target}
      aria-label={lockedForRelease ? `角色「${c.name}」即將上架` : signedIn ? `與角色「${c.name}」互動` : `登入以與角色「${c.name}」互動`}
      aria-disabled={lockedForRelease}
      tabIndex={lockedForRelease ? -1 : undefined}
      onClick={lockedForRelease ? (e) => e.preventDefault() : undefined}
      className={`group rounded-xl border border-slate-800 bg-slate-900 transition-all duration-200 ease-out flex flex-col focus:outline-none focus:ring-2 focus:ring-violet-500 overflow-hidden ${
        lockedForRelease
          ? 'cursor-not-allowed'
          : 'hover:border-violet-500/60 hover:-translate-y-0.5 hover:shadow-[0_12px_32px_-12px_rgba(124,70,243,0.55)] active:translate-y-0 active:scale-[0.99]'
      }`}
    >
      {/* Portrait 3/4 viewer on mobile (Tinder-card density — ~5
          cards visible per scroll instead of the old 2.3); 4/3 on
          larger screens where horizontal space is no longer the
          limiting factor. */}
      <div className="aspect-[3/4] sm:aspect-[4/3] bg-slate-950 relative overflow-hidden">
        {is3D && inView && publicInfo?.model3d?.modelPath ? (
          <Character3DStage
            info={publicInfo}
            interactive={false}
            framing="card-cover"
            className="w-full h-full"
          />
        ) : is3D && inView && publicInfoFailed ? (
          <SimpleGlbViewer
            url={c.modelPath!}
            rotationY={c.viewportRotationY ?? 0}
            clipLabels={c.clipLabels}
            defaultClipLabels={c.defaultClipLabels}
          />
        ) : is3D && inView ? (
          <div
            aria-hidden="true"
            className="w-full h-full nx-skeleton-shimmer"
          />
        ) : c.thumbnailUrl ? (
          <img
            src={c.thumbnailUrl}
            alt={`${c.name} 的縮圖`}
            className="w-full h-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04]"
            loading="lazy"
          />
        ) : (
          <div
            aria-hidden="true"
            className="w-full h-full flex items-center justify-center text-5xl text-slate-700"
          >{c.name.slice(0, 1)}</div>
        )}
        {/* Status badges */}
        <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 flex flex-col gap-1 items-end">
          {comingSoon && (
            <span className="rounded-full bg-violet-500/95 text-white text-[10px] px-2 py-0.5 font-medium shadow-lg">
              即將上架
            </span>
          )}
          {owned && (
            <span className="rounded-full bg-blue-500/90 text-white text-[10px] px-2 py-0.5 font-medium">
              已擁有
            </span>
          )}
          {!comingSoon && !owned && c.modelDistribution?.mode === 'paid' && (
            <span className="rounded-full bg-amber-500/90 text-slate-900 text-[10px] px-2 py-0.5 font-medium">
              {c.modelDistribution.pricePoints} 點
            </span>
          )}
          {!comingSoon && !owned && c.modelDistribution?.mode === 'free' && (
            <span className="rounded-full bg-slate-700/90 text-slate-200 text-[10px] px-2 py-0.5">
              免費
            </span>
          )}
        </div>
      </div>
      <div className="p-2 sm:p-3 flex-1 min-w-0">
        <div className="font-medium truncate text-sm sm:text-base">{c.name}</div>
        {c.description && (
          <div className="hidden sm:block text-slate-400 text-xs mt-0.5">
            {c.description.length > 30 ? c.description.slice(0, 30) + '…' : c.description}
          </div>
        )}
        <div className="text-slate-500 text-[10px] sm:text-xs mt-1 sm:mt-2 flex items-center gap-2">
          {c.accessReason === 'public' && <span>公開</span>}
          {c.accessReason === 'my-group' && <span className="text-blue-400">我的群組</span>}
          {c.accessReason === 'my-individual' && <span className="text-blue-400">已授權</span>}
          {c.accessReason === 'owned' && <span className="text-blue-400">已擁有</span>}
          {/* 右下角:角色所屬經營者(企業式名稱)tag */}
          {c.operatorDisplayName && (
            <span
              className="ml-auto min-w-0 truncate max-w-[62%] rounded bg-slate-800/80 px-1.5 py-0.5 text-[10px] text-slate-300"
              title={c.operatorDisplayName}
            >
              {c.operatorDisplayName}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export function Landing() {
  useDocumentTitle(null);
  const signedIn = Boolean(getStoredFanClaims()?.sub);
  const configured = fanCognitoConfigured();
  const [tab, setTab] = useState<Tab>(signedIn ? 'mine' : 'all');
  const [allChars, setAllChars] = useState<FanCharacterCard[] | null>(null);
  const [ownedIds, setOwnedIds] = useState<Set<string> | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const out = await listFanCharacters();
        if (alive) setAllChars(out.items ?? []);
      } catch (e) {
        if (alive) setErr((e as Error).message);
      }
    })();
    if (signedIn) {
      (async () => {
        try {
          const out = await listMyOwnedCharacters();
          if (alive) setOwnedIds(new Set(out.items.map((it) => it.characterId)));
        } catch {
          if (alive) setOwnedIds(new Set());
        }
      })();
    } else {
      setOwnedIds(new Set());
    }
    return () => { alive = false; };
  }, [signedIn]);

  const myChars = useMemo(() => {
    if (!allChars || !ownedIds) return null;
    return allChars.filter((c) => ownedIds.has(c.id));
  }, [allChars, ownedIds]);

  // Auto-fall back to "all" tab when "mine" is empty (user explicitly
  // asked for this UX: "如果使用者沒有任何角色，才會引導到這個選項")
  useEffect(() => {
    if (signedIn && tab === 'mine' && myChars && myChars.length === 0 && allChars && allChars.length > 0) {
      setTab('all');
    }
  }, [signedIn, tab, myChars, allChars]);

  const loadingMine = signedIn && (allChars === null || ownedIds === null);
  const loadingAll = allChars === null;
  const displayChars = tab === 'mine' ? myChars : allChars;

  // Anonymous visitors get a full marketing landing with hero +
  // feature sections + character grid preview. Signed-in visitors
  // skip straight to their character grid (their daily experience).
  if (!signedIn) {
    return <UnsignedLanding configured={configured} allChars={allChars} loadingAll={loadingAll} err={err} />;
  }

  return (
    <div className="max-w-5xl mx-auto px-2 sm:px-4 py-3 sm:py-6">

      {/* Tab switcher — only meaningful when signed in */}
      {signedIn && (
        <div className="mb-4 flex gap-1 border-b border-slate-800">
          {([
            ['mine', '我的角色', myChars?.length ?? '—'],
            ['all', '角色列表', allChars?.length ?? '—'],
          ] as Array<[Tab, string, number | string]>).map(([key, label, count]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm border-b-2 transition flex items-center gap-2 ${
                tab === key ? 'border-blue-500 text-blue-400' : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              <span>{label}</span>
              <span className="text-xs text-slate-500">{count}</span>
            </button>
          ))}
        </div>
      )}

      {err && <div role="alert" className="text-red-400 text-sm mb-4">{err}</div>}

      {/* "我的角色" empty state */}
      {signedIn && tab === 'mine' && myChars && myChars.length === 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 text-center mb-6">
          <p className="text-slate-300 mb-2">你還沒有任何角色</p>
          <p className="text-slate-500 text-sm mb-4">
            到「角色列表」找喜歡的角色，加入或購買後就會出現在這裡。
          </p>
          <button
            type="button"
            onClick={() => setTab('all')}
            className="rounded-full bg-violet-500 px-4 py-1.5 text-white text-sm hover:bg-violet-400"
          >去逛角色列表</button>
        </div>
      )}

      {/* "角色列表" empty state */}
      {tab === 'all' && allChars && allChars.length === 0 && (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 text-center">
          <p className="text-slate-300">目前沒有公開角色可瀏覽。</p>
          <p className="text-slate-500 text-sm mt-1">
            {signedIn
              ? '等到 IP 經營者上架新角色之後，會自動出現在這裡。'
              : '登入後可能會看到專屬於你的角色。'}
          </p>
        </div>
      )}

      {/* Grid — 2-col on mobile (Tinder-card density), 3-col tablet,
          4-col desktop. Waterfall lazy-load via IntersectionObserver
          inside CharacterTile. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
        {(loadingMine || loadingAll) && tab === 'mine' && Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)}
        {(loadingAll) && tab === 'all' && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        {displayChars?.map((c) => (
          <CharacterTile
            key={c.id}
            c={c}
            owned={ownedIds?.has(c.id) ?? false}
            signedIn={signedIn}
          />
        ))}
      </div>
    </div>
  );
}

// ── Unsigned visitor: marketing landing ──────────────────────────
// Hero (above the fold) + 4 feature scroll sections + character grid
// preview at the bottom (tile click → /login?next=...). Per the
// `fan-spa-landing-conversion-ux` OpenSpec change.

interface UnsignedLandingProps {
  configured: boolean;
  allChars: FanCharacterCard[] | null;
  loadingAll: boolean;
  err: string | null;
}

function UnsignedLanding({ configured, allChars, loadingAll, err }: UnsignedLandingProps) {
  const nav = useNavigate();
  const startCta = (label: string, primary = true): React.ReactNode => (
    <button
      type="button"
      onClick={() => nav('/signup')}
      className={primary
        ? 'inline-flex items-center justify-center rounded-full bg-violet-500 px-6 py-3 text-base font-medium text-white shadow-lg shadow-violet-500/20 transition hover:bg-violet-400'
        : 'inline-flex items-center justify-center rounded-full border border-violet-500/40 bg-violet-500/10 px-5 py-2 text-sm font-medium text-violet-200 transition hover:bg-violet-500/20'
      }
    >{label}</button>
  );

  return (
    <div className="w-full">
      {/* HERO — full viewport on mobile, big band on desktop */}
      <section className="relative w-full min-h-[88vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-violet-950/40">
        {/* Background flair — subtle radial glows */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 py-12 text-center">
          <div className="text-[11px] font-medium uppercase tracking-[0.3em] text-violet-300 mb-3">
            Next Companion Operating Platform
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight text-slate-50 mb-5 leading-tight">
            跟 AI 角色，<br />一起經營你們的故事
          </h1>
          <p className="text-base sm:text-lg text-slate-300/90 mb-8 max-w-xl mx-auto leading-relaxed">
            3D 角色 × 即時對談 × 物件收藏 × 心情模擬 — 沉浸於 NOXOR 的 AI 角色世界。
          </p>
          {configured ? (
            <div className="flex items-center justify-center gap-3 flex-wrap">
              {startCta('立即註冊體驗')}
              <button
                type="button"
                onClick={() => nav('/login')}
                className="text-sm text-slate-300 hover:text-slate-100"
              >已有帳號？登入 →</button>
            </div>
          ) : (
            <p className="text-amber-300 text-sm">此部署尚未設定登入；請聯絡管理員。</p>
          )}
          <div className="mt-12 animate-bounce text-slate-500 text-2xl" aria-hidden="true">↓</div>
        </div>
      </section>

      {/* FEATURE SECTIONS */}
      <FeatureSection
        icon="💬"
        title="與 AI 角色自然對談"
        description="用聲音或文字跟有個性的 AI 角色聊天 — 不是制式回應，每個角色都有自己的喜好、厭惡、敏感詞。"
        ctaSlot={configured ? startCta('立即註冊體驗', false) : null}
      />
      <FeatureSection
        icon="🌅"
        title="角色擁有自己的心情與生活"
        description="每隻角色都會隨時間改變心情、發生事件 — 你今天上線時看到的，跟昨天的他不一樣。"
        ctaSlot={configured ? startCta('立即註冊體驗', false) : null}
        invertBg
      />
      <FeatureSection
        icon="🛍️"
        title="收藏專屬角色物件"
        description="解鎖、收藏限定的 skin 與配件 — 跨角色通用，把你買的物件穿到喜歡的角色身上。"
        ctaSlot={configured ? startCta('立即註冊體驗', false) : null}
      />
      <FeatureSection
        icon="🤝"
        title="跟運營者共同經營"
        description="運營者持續更新角色設定、心情邏輯、限定活動 — 你跟你喜歡的創作者一起，把角色養大。"
        ctaSlot={configured ? startCta('立即註冊體驗', false) : null}
        invertBg
      />

      {/* CHARACTER GRID PREVIEW */}
      <section className="w-full bg-slate-950 py-12 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-100 mb-2 text-center">已上線的角色</h2>
          <p className="text-slate-400 text-sm text-center mb-8">
            點任一角色卡片開始體驗 — 註冊後即可解鎖。
          </p>
          {err && <div role="alert" className="text-red-400 text-sm mb-4 text-center">{err}</div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
            {loadingAll && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
            {allChars?.slice(0, 12).map((c) => (
              <CharacterTile key={c.id} c={c} owned={false} signedIn={false} />
            ))}
          </div>
          {allChars && allChars.length === 0 && (
            <div className="rounded-xl border border-slate-800 bg-slate-900 p-6 text-center mt-4">
              <p className="text-slate-400">尚未有公開上架的角色，敬請期待。</p>
            </div>
          )}
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="w-full bg-gradient-to-br from-violet-950/40 to-slate-950 py-16 px-4 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-50 mb-3">準備好認識他們了嗎？</h2>
        <p className="text-slate-300 mb-6">註冊免費，馬上開始你的角色經營旅程。</p>
        {configured && startCta('立即註冊體驗')}
      </section>
    </div>
  );
}

interface FeatureSectionProps {
  icon: string;
  title: string;
  description: string;
  ctaSlot: React.ReactNode | null;
  invertBg?: boolean;
}

function FeatureSection({ icon, title, description, ctaSlot, invertBg }: FeatureSectionProps) {
  return (
    <section className={`w-full py-14 sm:py-20 px-4 sm:px-6 ${invertBg ? 'bg-slate-900' : 'bg-slate-950'}`}>
      <div className="max-w-3xl mx-auto text-center">
        <div className="text-5xl sm:text-6xl mb-4" aria-hidden="true">{icon}</div>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-50 mb-3 tracking-tight">{title}</h2>
        <p className="text-base sm:text-lg text-slate-300/90 mb-6 leading-relaxed">{description}</p>
        {ctaSlot}
      </div>
    </section>
  );
}
